import _ from "lodash";
import React, { useEffect, useState } from "react";

// Components
import FieldSelectionModal from "./FieldSelectionModal";

// DS
import { Button, Tooltip } from "@toddle-edu/ds-web";
import { TickCircleFilled } from "@toddle-edu/ds-icons";

// Utils
import { getFieldValue } from "Assessments/modules/Utils";
import {
  getCurrentFieldConfig,
  getFieldHasValue,
  getIsMYPInterdiscplinaryUnit,
  getEntityAndFieldType,
  getAssessmentToolFromField,
  getHeaderText,
  getGroupedFields,
} from "./Utils";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { scrollIntoView } from "Utils";

// Constants
import { ASSESSMENT } from "Constants/stringConstants";

// third party
import { connect } from "react-redux";
import classNames from "classnames";
import { compose } from "lib/apolloClient";

// modules
import { getCurrentCurriculumProgramType } from "modules/CommonSelectors";
import { updateAssessmentInRedux } from "Assessments/modules/AssessmentModule";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { updateAIMessage } from "modules/Services";
import { getAssessmentObj } from "ClassRoom/modules/ClassRoomModule";

// Scss
import classes from "./PrerequisiteSelection.scss";

const PrerequisiteSelection = props => {
  const {
    t,
    context,
    messages,
    fieldList,
    curriculumType,
    assessmentType,
    unitPlanDetails,
    unitPlanTemplate,
    setSelectedAction,
    assessmentDetails,
    prerequisiteFieldsData,
    isEveryPrerequisiteFieldFilled,
    excludedFields,
    setExcludedFields,
    fieldsToRemove,
    updateAssessmentInRedux,
    measureAssessingRef,
    showAssessmentToolModal,
    assessmentTools,
    setToastMsg,
    groupedFields,
    updateAIMessage,
    stepList,
    getAssessmentObj,
    isQuestionAssistant,
  } = props;

  const [selectedField, setSelectedField] = useState({});
  const [isAssessmentToolCreateMode, setIsAssessmentToolCreateMode] = useState(
    false
  );

  // when all the prerequisites are filled and then again generate AI response with fieldType and action
  useEffect(() => {
    const skipGPTCall = _.get(prerequisiteFieldsData, "skipGPTCall", false);

    const generateAIResponse =
      !skipGPTCall &&
      isEveryPrerequisiteFieldFilled &&
      _.isEmpty(selectedField?.fields);

    if (generateAIResponse) {
      const lastUserMessage = _.findLast(
        messages,
        ({ node } = {}) => node?.authorType === "user"
      );

      const displayText = _.get(lastUserMessage, "node.content", "");

      // 500 ms delay to show fadeout animation (which is also of 500ms)
      setTimeout(() => {
        setSelectedAction({
          ..._.pick(prerequisiteFieldsData, ["fieldType", "action"]),
          displayText,
          createUserMessage: false,
        });
      }, 500);
    }
  }, [isEveryPrerequisiteFieldFilled, selectedField]);

  // if user skipped some fields and then later on added data for them then remove those fields from excludedFields
  useEffect(() => {
    if (!_.isEmpty(fieldsToRemove)) {
      const updatedExcludedFields = _.filter(
        excludedFields,
        excludedField => !_.includes(fieldsToRemove, excludedField)
      );

      setExcludedFields(updatedExcludedFields);
    }
  }, [fieldsToRemove, excludedFields]);

  // when assessment tool is added then scroll into its view
  useEffect(() => {
    // if assessment tool modal is open then do not trigger scroll into view
    if (showAssessmentToolModal) return;

    if (isAssessmentToolCreateMode && !_.isEmpty(assessmentTools)) {
      _.forEach(prerequisiteFieldsData?.fields, fields => {
        _.forEach(fields, ({ field }) => {
          const assessmentTool = getAssessmentToolFromField({ field });
          if (!_.isEmpty(assessmentTool)) {
            const { type: assessmentToolType, locale } = assessmentTool || {};

            const isToolAdded = getFieldHasValue({
              assessmentDetails,
              field,
            });

            if (isToolAdded) {
              scrollIntoView({
                element: document.getElementById(`${assessmentToolType}`),
              });

              setToastMsg({
                msg: "toastMsgs:successfully_added_to_this_task_with_label",
                locale_params: [
                  {
                    key: "label",
                    value: locale,
                    isPlainText: false,
                  },
                ],
                type: "success",
              });

              setIsAssessmentToolCreateMode(false);
            }
          }
        });
      });
    }
  }, [isAssessmentToolCreateMode, showAssessmentToolModal, assessmentTools]);

  // groupedFields can change based on unit selection, so initially if user had not selected the unit then later selected it
  // we need to check if new and old pre-requisite are not the same, if they aren't they update the AI message with latest pre-requisite
  // this was an edge case but causing multiple call rn debug this later
  // useEffect(() => {
  //   if (!_.isEqual(groupedFields, prerequisiteFieldsData?.fields)) {
  //     const lastPrerequisiteMsg = _.findLast(
  //       messages,
  //       ({ node } = {}) => node?.authorType === "prerequisite"
  //     );

  //     const id = _.get(lastPrerequisiteMsg, "node.id");

  //     if (id) {
  //       updateAIMessage({
  //         variables: {
  //           input: {
  //             id,
  //             metadata: {
  //               ...prerequisiteFieldsData,
  //               fields: groupedFields,
  //             },
  //           },
  //         },
  //       });
  //     }
  //   }
  // }, [groupedFields, prerequisiteFieldsData]);

  // variables
  const typeObj = getAssessmentObj({
    curriculumType,
    assessmentValue: assessmentType,
    contentType: ASSESSMENT,
  });

  const { typeLabel, typeLabel2 } = typeObj || {};
  const assessmentTypeLabel = typeLabel || typeLabel2;

  const { field_list: unitPlanFieldList, step_list: unitPlanStepList } =
    unitPlanTemplate || {};

  // functions
  const onBlockClick = params => {
    const { key, field, step, disabled, isUnitDependent, config } = params;
    const { unitPlanFields } = unitPlanDetails || {};

    let fields = [{ field, config }];

    switch (field) {
      case "content": {
        if (getIsMYPInterdiscplinaryUnit(unitPlanFields)) {
          fields = [{ field: "disciplinaryContent", config }];
        }
        break;
      }
      case "title": {
        if (key === "unitTitle") {
          const unitPlanValue = getFieldValue({
            fields: assessmentDetails?.allFields,
            uid: "unitPlan",
          });

          const unitPlanTitle = getFieldValue({
            fields: unitPlanFields,
            uid: "title",
          });

          if (_.isEmpty(unitPlanValue)) {
            fields = [{ field: "unitPlan", config }];
          } else if (_.isEmpty(unitPlanTitle)) {
            fields = [{ field: "title", config }];
          }
        }
        break;
      }
      default: {
        const assessmentTool = getAssessmentToolFromField({ field });
        if (!_.isEmpty(assessmentTool)) {
          const assessmentToolType = _.get(assessmentTool, "type");

          const isToolAdded = getFieldHasValue({
            assessmentDetails,
            field,
          });

          // if tool is already created then open edit modal
          if (isToolAdded) {
            const currentAssessmentTool = _.find(
              _.get(assessmentDetails, "assessmentTools.edges", []),
              ({ node }) =>
                _.get(node, "assessmentToolType") === assessmentToolType
            );

            measureAssessingRef?.updateAssessmentToolData?.({
              assessmentToolModalMode: "edit_tool",
              showAssessmentToolModal: true,
            });

            updateAssessmentInRedux({
              assessmentToolId: _.get(
                currentAssessmentTool,
                "node.assessmentToolItem.id"
              ),
              assessmentToolType,
            });
          } else {
            // create a new tool
            const params = {
              key: assessmentToolType,
              keyPath: [assessmentToolType],
            };

            measureAssessingRef?.onChangeDropDownMenuOption?.(params);

            setIsAssessmentToolCreateMode(true);
          }

          return;
        }
      }
    }

    if (!disabled) {
      const _stepList = isUnitDependent ? unitPlanStepList : stepList;

      // if step is defined for any pre-requisite then render all the fields of step
      if (_stepList?.[step]) {
        const stepFields = _.get(_stepList, `${step}.fields`, []);
        setSelectedField({ fields: stepFields, step, isUnitDependent });
      } else {
        setSelectedField({ fields, step, isUnitDependent });
      }
    }
  };

  // if user has skipped any field then show it as skipped else show it as skip
  const onClickSkip = field => {
    if (_.includes(excludedFields, field)) {
      const updatedExcludedFields = _.filter(
        excludedFields,
        excludedField => excludedField !== field
      );

      setExcludedFields(updatedExcludedFields);
    } else {
      setExcludedFields(prevState => [...prevState, field]);
    }
  };

  // classes
  const containerClass = classNames({
    [classes.container]: true,
    [classes.containerFadeOut]:
      isEveryPrerequisiteFieldFilled && _.isEmpty(selectedField?.fields),
  });

  return (
    <div className={containerClass}>
      <div className={classes.headerText}>
        {getHeaderText({
          t,
          prerequisiteFieldsData,
        })}
      </div>

      {_.map(prerequisiteFieldsData?.fields, (fields, key) => {
        const isUnitDependent = key === "UNIT";

        const entityLabel = isQuestionAssistant
          ? t("common:question")
          : isUnitDependent
          ? t("common:unit")
          : t("common:capitalize", {
              text: t(assessmentTypeLabel),
            });

        return (
          <div key={key} className={classes.blockContainer}>
            <div className={classes.blockHeader}>
              {t("classRoom:assessment_details", {
                label: entityLabel,
              })}
            </div>

            {_.map(fields, currentField => {
              const { field: key, type, step, config, isSkippable } =
                currentField || {};

              const [entity, field] = getEntityAndFieldType({
                key,
                type,
                unitPlanDetails,
                assessmentDetails,
              });

              const isUnitDependent = entity === "UNIT";

              const fieldData = getCurrentFieldConfig({
                t,
                field,
                step,
                type,
                context,
                assessmentDetails,
                unitPlanDetails,
                entityLabel,
                isUnitDependent,
                fieldList,
                unitPlanFieldList,
                stepList,
                unitPlanStepList,
                fieldConfig: config,
                isSkippable,
              });

              const {
                disabled,
                disabledTooltip,
                hasValue,
                label,
                buttonLabel,
                showSkipButton,
                showField = true,
              } = fieldData || {};
              const blockClass = classNames({
                [classes.block]: true,
                [classes.disabledBlock]: disabled,
              });

              if (!showField) return null;

              return (
                <div
                  key={field}
                  className={blockClass}
                  onClick={() => {
                    onBlockClick({
                      key,
                      field,
                      disabled,
                      isUnitDependent,
                      step,
                      config,
                    });
                  }}
                >
                  <div className={classes.blockLeftContainer}>{label}</div>
                  <div className={classes.blockRightContainer}>
                    {showSkipButton ? (
                      _.includes(excludedFields, type) ? (
                        <Button
                          icon={<TickCircleFilled variant={"success"} />}
                          variant={"inline-progressive"}
                          size={"small"}
                          onClick={() => onClickSkip(type)}
                          shouldStopPropagation={true}
                          testId={
                            "worksheet-worksheetAssistant-PrerequisiteSelection-skipped"
                          }
                        >
                          {t("common:skipped")}
                        </Button>
                      ) : (
                        <Button
                          variant={"inline"}
                          size={"small"}
                          onClick={() => onClickSkip(type)}
                          shouldStopPropagation={true}
                          testId={
                            "worksheet-worksheetAssistant-PrerequisiteSelection-skip"
                          }
                        >
                          {t("common:skip")}
                        </Button>
                      )
                    ) : null}

                    {hasValue ? (
                      <Button
                        icon={<TickCircleFilled variant={"success"} />}
                        size={"small"}
                        variant={"on"}
                      >
                        <Tooltip tooltip={buttonLabel}>
                          <div className={classes.selectedButton}>
                            {buttonLabel}
                          </div>
                        </Tooltip>
                      </Button>
                    ) : (
                      <ConditionalWrapper
                        type={"wrapperAsElement"}
                        condition={disabled && disabledTooltip}
                        wrapper={
                          <Tooltip
                            tooltip={disabledTooltip}
                            placement={"bottom"}
                          />
                        }
                      >
                        <Button
                          size={"small"}
                          variant={"on"}
                          disabled={disabled}
                        >
                          <Tooltip tooltip={buttonLabel}>
                            <span>{buttonLabel}</span>
                          </Tooltip>
                        </Button>
                      </ConditionalWrapper>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}

      {!_.isEmpty(selectedField?.fields) ? (
        <FieldSelectionModal
          {...props}
          {...(selectedField || {})}
          onClose={() => setSelectedField({})}
          assessmentTypeLabel={assessmentTypeLabel}
        />
      ) : null}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const {
    t,
    context,
    assessmentDetails,
    prerequisiteFieldsData,
    unitPlanDetails,
    fieldList,
    unitPlanTemplate,
    excludedFields,
    middlePaneRef,
    stepList,
  } = ownProps;

  const assessmentType = getFieldValue({
    fields: assessmentDetails?.allFields,
    uid: "assessmentType",
  });

  const assessmentTools = _.get(assessmentDetails, "assessmentTools.edges", []);

  const fieldsToRemove = [];

  const isEveryPrerequisiteFieldFilled = _.every(
    _.flattenDeep(_.map(prerequisiteFieldsData?.fields, _.values)),
    ({ field: key, type, step }) => {
      const [entity, field] = getEntityAndFieldType({
        key,
        type,
        unitPlanDetails,
        assessmentDetails,
      });

      const fieldHasValue = getFieldHasValue({
        field,
        step,
        context,
        fieldList,
        stepList,
        unitPlanFieldList: _.get(unitPlanTemplate, "field_list", {}),
        unitPlanStepList: _.get(unitPlanTemplate, "step_list", {}),
        assessmentDetails,
        isUnitDependent: entity === "UNIT",
        unitPlanDetails,
      });

      const isExcluded = _.includes(excludedFields, type);

      // if field was skipped but it contains value then remove it from excludedFields
      if (fieldHasValue && isExcluded) fieldsToRemove.push(field);

      // no need to check value for excluded fields
      if (isExcluded) return true;

      return fieldHasValue;
    }
  );

  const measureAssessingRef = middlePaneRef?.Refs?.measureAssessing;
  const showAssessmentToolModal =
    measureAssessingRef?.state?.assessmentToolData?.showAssessmentToolModal;

  const groupedFields = getGroupedFields({
    t,
    fields: _.flatten(_.values(prerequisiteFieldsData?.fields)),
    fieldList,
    unitPlanFieldList: _.get(unitPlanTemplate, "field_list", {}),
    unitPlanDetails,
    assessmentDetails,
    stepList,
    unitPlanStepList: _.get(unitPlanTemplate, "step_list", {}),
  });

  return {
    unitPlanDetails,
    isEveryPrerequisiteFieldFilled,
    assessmentType,
    curriculumType: getCurrentCurriculumProgramType(state),
    fieldsToRemove,
    measureAssessingRef,
    showAssessmentToolModal,
    assessmentTools,
    groupedFields,
  };
};

const mapActionCreators = {
  updateAssessmentInRedux,
  setToastMsg,
  updateAIMessage,
  getAssessmentObj,
};

export default compose(connect(mapStateToProps, mapActionCreators))(
  PrerequisiteSelection
);
